import { TestIds } from '@/testIds'
import type { TripDayInfo } from '@hooks/useTripDayInfos'
import { BottomBar } from '@pages/Trips/CreateTripFlow/core/presentation'
import FlowShell from '@pages/Trips/CreateTripFlow/FlowShell'
import { Trip } from '@types'
import React, { useMemo } from 'react'
import { DayHeaderInput, dayLine, destinationSegments } from '../core/dayHeader'
import { EventCreateDraft } from './core/draft'
import {
	EventCreateStep,
	STEPS,
	TripDays,
	canGoTo,
	daySummary,
	nextStep,
	previousStep,
	showsHeaderBack,
	stepIndicatorState,
	stepLabel,
} from './core/presentation'
import ExtrasStep from './ExtrasStep'
import WhatStep from './WhatStep'
import WhenStep from './WhenStep'

interface EventFlowProps {
	/** The header's centre — "New Event", "Edit Event". */
	title: string
	trip: Trip
	dayInfos: TripDayInfo[] | undefined
	draft: EventCreateDraft
	onChange: (draft: EventCreateDraft) => void
	step: EventCreateStep
	onStepChange: (step: EventCreateStep) => void
	/** The flow's bottom bar, given the draft's day in one line. */
	bar: (daySummary: string) => BottomBar
	ctaPending: boolean
	/** The CTA on the last step; Next on the others walks the steps. */
	onSubmit: () => void
	onClose: () => void
}

/**
 * The What → When → Extras pages the create- and edit-event flows share, on
 * the create-trip flow's chrome: back · title · close, the labelled step
 * indicator, the step's form and the bottom bar. Each flow supplies its own
 * title, bar and submit, as Android's `EventFlowView`.
 */
const EventFlow: React.FC<EventFlowProps> = ({
	title,
	trip,
	dayInfos,
	draft,
	onChange,
	step,
	onStepChange,
	bar,
	ctaPending,
	onSubmit,
	onClose,
}) => {
	const segments = useMemo(() => destinationSegments(trip), [trip])
	const days: TripDays = useMemo(
		() => ({ startDate: trip.startDate, dayCount: trip.dayCount, segments }),
		[trip, segments],
	)

	const day: DayHeaderInput = {
		dayIndex: draft.tripDayIndex,
		startDate: trip.startDate,
		segments,
		dayName: dayInfos?.find(info => info.dayIndex === draft.tripDayIndex)?.name,
	}

	const goTo = (target: EventCreateStep) => {
		if (canGoTo(draft, target)) onStepChange(target)
	}

	const next = nextStep(step)

	return (
		<FlowShell
			title={title}
			showsHeaderBack={showsHeaderBack(step)}
			onBack={() => {
				const previous = previousStep(step)
				if (previous) onStepChange(previous)
			}}
			onClose={onClose}
			steps={STEPS.map(candidate => ({
				key: candidate,
				label: stepLabel(candidate),
				state: stepIndicatorState(draft, candidate, step),
			}))}
			onSelectStep={goTo}
			bar={bar(daySummary(day))}
			ctaPending={ctaPending}
			ctaTestId={next ? TestIds.itinerary.eventNextButton : TestIds.itinerary.eventSubmitButton}
			onCta={() => (next ? goTo(next) : onSubmit())}
		>
			{step === 'what' && <WhatStep tripId={String(trip.id)} draft={draft} onChange={onChange} />}
			{step === 'when' && <WhenStep draft={draft} onChange={onChange} days={days} dayLine={dayLine(day)} />}
			{step === 'extras' && <ExtrasStep draft={draft} onChange={onChange} />}
		</FlowShell>
	)
}

export default EventFlow
